import { Button, ErrorMessage, Skeleton } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { GraphQLService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import {
  updateWorkspaceMemberPrivateStorageLimitMutation,
  workspacePrivateStorageUsageQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import bytes from 'bytes';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from './style.css';

const GIB = 1024 * 1024 * 1024;
const UNKNOWN_STORAGE_VALUE = '...';

interface PrivateStorageMember {
  userId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  workspaceRole: string;
  isGuest: boolean;
  usedBytes: number;
  limitBytes: number;
  percent: number;
}

interface PrivateStoragePayload {
  schemaVersion: 1;
  minLimitBytes: number;
  defaultLimitBytes: number;
  maxLimitBytes: number;
  members: PrivateStorageMember[];
}

interface UpdatePrivateStoragePayload {
  schemaVersion: 1;
  member: PrivateStorageMember;
}

const formatBytes = (value: number) => bytes.format(value) ?? '0 B';

function toPayload(value: unknown): PrivateStoragePayload {
  return value as PrivateStoragePayload;
}

function toUpdatePayload(value: unknown): UpdatePrivateStoragePayload {
  return value as UpdatePrivateStoragePayload;
}

export const PrivateStorageAdminPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const workspaceId = useService(WorkspaceService).workspace.id;
  const graphql = useService(GraphQLService);
  const [data, setData] = useState<PrivateStoragePayload | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const backendUnavailableMessage =
    t['com.streamlain.settings.connection-required.description']();

  const loadUsage = useCallback(async () => {
    if (!backendAvailable) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await graphql.gql({
        query: workspacePrivateStorageUsageQuery,
        variables: { workspaceId },
      });
      const payload = toPayload(result.workspacePrivateStorageUsage);
      markBackendAvailable();
      setData(payload);
      setDrafts(
        Object.fromEntries(
          payload.members.map(member => [
            member.userId,
            String(member.limitBytes / GIB),
          ])
        )
      );
    } catch (err) {
      if (isBackendUnavailableError(err)) {
        markBackendUnavailable(err);
      }
      setError(
        err instanceof Error
          ? err
          : new Error(
              isBackendUnavailableError(err)
                ? backendUnavailableMessage
                : String(err)
            )
      );
    } finally {
      setLoading(false);
    }
  }, [backendAvailable, backendUnavailableMessage, graphql, workspaceId]);

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  const minLimitGb = useMemo(
    () => (data ? data.minLimitBytes / GIB : 1),
    [data]
  );
  const maxLimitGb = useMemo(
    () => (data ? data.maxLimitBytes / GIB : 10),
    [data]
  );

  const saveLimit = useCallback(
    async (member: PrivateStorageMember) => {
      if (!data) return;
      if (!backendAvailable) {
        setError(new Error(backendUnavailableMessage));
        return;
      }

      const draftValue = Number(drafts[member.userId]);
      if (
        !Number.isFinite(draftValue) ||
        draftValue < minLimitGb ||
        draftValue > maxLimitGb
      ) {
        setError(
          new Error(
            t['com.streamlain.settings.workspace.storage.private.limit-error']({
              min: String(minLimitGb),
              max: String(maxLimitGb),
            })
          )
        );
        return;
      }

      setSavingUserId(member.userId);
      setError(null);
      try {
        const result = await graphql.gql({
          query: updateWorkspaceMemberPrivateStorageLimitMutation,
          variables: {
            workspaceId,
            userId: member.userId,
            limitBytes: Math.round(draftValue * GIB),
          },
        });
        const payload = toUpdatePayload(
          result.updateWorkspaceMemberPrivateStorageLimit
        );
        markBackendAvailable();
        setData(current =>
          current
            ? {
                ...current,
                members: current.members.map(row =>
                  row.userId === payload.member.userId ? payload.member : row
                ),
              }
            : current
        );
        setDrafts(current => ({
          ...current,
          [payload.member.userId]: String(payload.member.limitBytes / GIB),
        }));
      } catch (err) {
        if (isBackendUnavailableError(err)) {
          markBackendUnavailable(err);
        }
        setError(
          err instanceof Error
            ? err
            : new Error(
                isBackendUnavailableError(err)
                  ? backendUnavailableMessage
                  : String(err)
              )
        );
      } finally {
        setSavingUserId(null);
      }
    },
    [
      backendAvailable,
      backendUnavailableMessage,
      data,
      drafts,
      graphql,
      maxLimitGb,
      minLimitGb,
      t,
      workspaceId,
    ]
  );

  const backendUnavailable =
    !backendAvailable || (error !== null && isBackendUnavailableError(error));

  const renderTableHead = () => (
    <thead>
      <tr>
        <th>{t['com.streamlain.settings.workspace.storage.private.user']()}</th>
        <th>{t['com.streamlain.settings.workspace.storage.private.used']()}</th>
        <th>
          {t['com.streamlain.settings.workspace.storage.private.limit']()}
        </th>
      </tr>
    </thead>
  );

  const renderUnavailablePrivateStorage = () => (
    <SettingRow
      name={t['com.streamlain.settings.workspace.storage.private.title']()}
      desc={t['com.streamlain.settings.workspace.storage.private.desc']()}
      spreadCol={false}
    >
      <div className={styles.privateStoragePanel}>
        <div className={styles.storageBreakdownTableWrapper}>
          <table className={styles.storageBreakdownTable}>
            {renderTableHead()}
            <tbody>
              <tr>
                <td colSpan={3}>{UNKNOWN_STORAGE_VALUE}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </SettingRow>
  );

  if (loading && !data && backendUnavailable) {
    return renderUnavailablePrivateStorage();
  }

  if (loading && !data) {
    return (
      <SettingRow
        name={t['com.streamlain.settings.workspace.storage.private.title']()}
        desc=""
        spreadCol={false}
      >
        <Skeleton height={120} />
      </SettingRow>
    );
  }

  if (!data && backendUnavailable) {
    return renderUnavailablePrivateStorage();
  }

  if (!data) {
    return (
      <SettingRow
        name={t['com.streamlain.settings.workspace.storage.private.title']()}
        desc=""
        spreadCol={false}
      >
        <ErrorMessage>
          {t['com.streamlain.settings.workspace.storage.private.error']()}
        </ErrorMessage>
      </SettingRow>
    );
  }

  return (
    <SettingRow
      name={t['com.streamlain.settings.workspace.storage.private.title']()}
      desc={t['com.streamlain.settings.workspace.storage.private.desc']()}
      spreadCol={false}
    >
      <div className={styles.privateStoragePanel}>
        {error && !backendUnavailable ? (
          <ErrorMessage>
            {error.message ||
              t['com.streamlain.settings.workspace.storage.private.error']()}
          </ErrorMessage>
        ) : null}
        <div className={styles.storageBreakdownTableWrapper}>
          <table className={styles.storageBreakdownTable}>
            {renderTableHead()}
            <tbody>
              {data.members.map(member => {
                const draftValue = Number(drafts[member.userId]);
                const isInvalid =
                  !Number.isFinite(draftValue) ||
                  draftValue < minLimitGb ||
                  draftValue > maxLimitGb;
                return (
                  <tr key={member.userId}>
                    <td>
                      <div className={styles.privateStorageUserCell}>
                        <span>{member.name}</span>
                        <span>{member.email}</span>
                      </div>
                    </td>
                    <td>
                      <div>
                        {formatBytes(member.usedBytes)}
                        <div className={styles.privateStorageProgress}>
                          <div
                            className={styles.privateStorageProgressBar}
                            style={{ width: `${member.percent}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className={styles.privateStorageLimitControl}>
                        <input
                          aria-label={t[
                            'com.streamlain.settings.workspace.storage.private.limit'
                          ]()}
                          min={minLimitGb}
                          max={maxLimitGb}
                          step={1}
                          type="number"
                          value={drafts[member.userId] ?? ''}
                          disabled={!backendAvailable}
                          onChange={event => {
                            setDrafts(current => ({
                              ...current,
                              [member.userId]: event.target.value,
                            }));
                          }}
                        />
                        <span>
                          {t[
                            'com.streamlain.settings.workspace.storage.private.gigabytes'
                          ]()}
                        </span>
                        <Button
                          disabled={
                            !backendAvailable ||
                            isInvalid ||
                            savingUserId === member.userId
                          }
                          title={
                            !backendAvailable
                              ? backendUnavailableMessage
                              : undefined
                          }
                          onClick={() => void saveLimit(member)}
                        >
                          {t[
                            'com.streamlain.settings.workspace.storage.private.save'
                          ]()}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </SettingRow>
  );
};
